import { html, TemplateResult, LitElement } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedNodes,
} from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import GuiDialog from './../../utilities/dialog';
import encapsulatedStyles from './styles/outline-dialog.encapsulated.pcss?inline';
import globalStyles from './styles/outline-dialog.global.pcss?inline';
import './../outline-icon/outline-icon';

@customElement('outline-dialog')
export class OutlineDialog extends LitElement {
  @query('.dialog-wrapper')
  dialogWrapper: HTMLDialogElement;

  @queryAssignedNodes({ slot: 'cta' })
  cta: NodeListOf<HTMLElement>;

  @queryAssignedNodes({ slot: 'dialog-content' })
  dialogContent: NodeListOf<HTMLElement>;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'dialog-open',
  })
  dialogOpen: boolean;

  @property({
    type: String,
    attribute: 'max-width',
  })
  maxWidth: string | null = '30rem';

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: boolean;

  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
    globalCSS: globalStyles,
  });

  updated() {
    this.cta?.forEach(cta => {
      if (cta instanceof HTMLElement) {
        cta.onclick = e => {
          e.preventDefault();
          this.openDialog();
        };
      }
    });

    this.dialogOpen && this.openDialog();
  }

  render(): TemplateResult {
    const classes = {
      'outline-dialog': true,
      'x-padding': this.xPadding,
    };

    const maxWidthStyle = this.maxWidth ? { 'max-width': this.maxWidth } : {};

    return html`
      <div class="${classMap(classes)}">
        <slot name="cta"></slot>
        <dialog class="dialog-wrapper">
          <div class="button-wrapper">
            <button
              type="button"
              class="close-button"
              @click="${this.closeDialog}"
              aria-label="close"
            >
              <outline-icon 
                size="var(--spacing-6)" 
                color="var(--bjc-blue)" 
                icon='<i class="fa-solid fa-xmark"></i>'>
              </outline-icon>
            </button>
          </div>
          <div class="content" style="${styleMap(maxWidthStyle)}">
            <slot name="dialog-content"></slot>
          </div>
        </dialog>
      </div>
    `;
  }

  openDialog() {
    GuiDialog(this.dialogWrapper);
    this.dialogWrapper.showModal();
    this.trapFocus();
  }

  closeDialog(e: Event) {
    this.dialogOpen = false;
    const trigger = e.currentTarget as HTMLDialogElement;
    const dialogElement = trigger.closest('dialog') as HTMLDialogElement;
    dialogElement.close('cancel');
  }

  trapFocus(): void {
    // Define a selector that matches elements that can be focused by default
    const defaultFocusableSelector =
      'a[href], button, input, select, textarea, [tabindex]';

    // Query all the elements that match the selector in the modal content
    const focusableNodes = this.dialogContent[0]?.querySelectorAll(
      defaultFocusableSelector
    );

    // Query the close button which is out of the modal content
    const closeCta = this.dialogWrapper?.querySelectorAll(
      defaultFocusableSelector
    );

    const focusableElements = Array.from(focusableNodes);
    // Add the close button to the focusable elements array, and fist element to be focused
    focusableElements.unshift(closeCta[0] as HTMLElement);

    const firstFocusableElement =
      focusableElements && (focusableElements[0] as HTMLElement);
    const lastFocusableSlottedElement =
      focusableElements &&
      (focusableElements[focusableElements.length - 1] as HTMLElement);

    if (firstFocusableElement) {
      const lastFocusableElement =
        lastFocusableSlottedElement ?? firstFocusableElement;

      if (lastFocusableElement) {
        lastFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey === false) {
              event.preventDefault();
              firstFocusableElement.focus();
            }
          }
        );
      }

      if (firstFocusableElement) {
        firstFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey) {
              event.preventDefault();
              if (lastFocusableElement) {
                lastFocusableElement.focus();
              }
            }
          }
        );
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-dialog': OutlineDialog;
  }
}